import {
  Box,
  Button,
  Flex,
  Icon,
  Skeleton,
  Text,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';

import { FaFileCirclePlus, FaWandMagicSparkles } from 'react-icons/fa6';

import { getFullName } from '../../helpers';
import { useClientCenterCustomerProfile } from '../../hooks';

interface ClientCenterHeaderProps {
  customerId: string;
}

interface HeaderSegment {
  key: string;
  show: boolean;
  loading?: boolean;
  width: string;
  render: () => JSX.Element;
}

const ClientCenterHeader = ({ customerId }: ClientCenterHeaderProps) => {
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.900', 'white');

  const { profile, policyTypes, isLoading, isPolicyTypesLoading } =
    useClientCenterCustomerProfile(customerId);

  const fullName = profile && getFullName(profile);

  const segments: HeaderSegment[] = [
    {
      key: 'name',
      show: isLoading || Boolean(fullName),
      width: '120px',
      render: () => (
        <Text color={textColor} fontWeight="600">
          {fullName}
        </Text>
      ),
    },
    {
      key: 'policy_types',
      show: isPolicyTypesLoading || policyTypes.length > 0,
      loading: isPolicyTypesLoading,
      width: '140px',
      render: () => <Text color={labelColor}>{policyTypes.join(' & ')}</Text>,
    },
    {
      key: 'managed_by',
      show: isLoading || Boolean(profile?.handler_name),
      width: '110px',
      render: () => <Text color={labelColor}>{profile?.handler_name}</Text>,
    },
  ];

  const visibleSegments = segments.filter(({ show }) => show);

  return (
    <Flex
      justify="space-between"
      align={{ base: 'flex-start', md: 'center' }}
      direction={{ base: 'column', md: 'row' }}
      gap="4"
      mb="6"
    >
      <Flex align="center" gap="2" wrap="wrap" fontSize="sm" minH="20px">
        {visibleSegments.map(
          ({ key, width, loading = isLoading, render }, index) => (
            <Flex key={key} align="center" gap="2">
              {index > 0 && <Text color={labelColor}>·</Text>}
              {loading ? (
                <Skeleton height="14px" width={width} borderRadius="sm" />
              ) : (
                render()
              )}
            </Flex>
          ),
        )}
      </Flex>

      <Flex gap="3" flexShrink={0}>
        <Tooltip label="Coming soon" placement="bottom" hasArrow>
          <Box>
            <Button
              variant="outline"
              size="md"
              leftIcon={<Icon as={FaFileCirclePlus} />}
              isDisabled
            >
              New Quote
            </Button>
          </Box>
        </Tooltip>

        <Tooltip label="Coming soon" placement="bottom" hasArrow>
          <Box>
            <Button
              variant="brand"
              size="md"
              leftIcon={<Icon as={FaWandMagicSparkles} />}
              isDisabled
            >
              Make a Change
            </Button>
          </Box>
        </Tooltip>
      </Flex>
    </Flex>
  );
};

export default ClientCenterHeader;
